import React from 'react';
import { Check } from 'lucide-react';
import SETTING_KEYS from '../../../constants/setting-keys';
import type { RegionalInfo, ISource } from '../../../store/implant-context';
import { formatMRIParameters } from '../../reports/cpt/utils/cptDocumentViewUtils';
import { stripHtml } from '../../documents/utils/documentViewUtils';
import { renderConditionalsListValue } from '../../preview/conditionalsLists';
import { cn } from '../../../lib/utils';
import { resolveLastCheckedDate } from '../implantDateUtils';
import {
    formatPreviewDate,
    PreviewRichText,
    previewTheme,
    type PreviewFieldData,
    type PreviewHeaderConfig,
    type PreviewSectionData,
} from '@nordinsight/implant-core/ui';

type TFunc = (key: string, opts?: Record<string, any>) => string;

// Render a field value as rich text when it contains HTML markup, otherwise as
// a plain string. Keeps custom fields, notes and other rich-text-authored
// fields formatted in the preview/print instead of showing raw HTML tags.
const renderFieldValue = (value: unknown): React.ReactNode | string => {
    if (typeof value !== 'string') return JSON.stringify(value);
    return /<[a-z][\s\S]*>/i.test(value) ? (
        <PreviewRichText html={value} />
    ) : (
        value
    );
};

export interface ImplantPreviewInput {
    info: any;
    regionalInfo: RegionalInfo;
    conditionals: any;
    approvalInfo?: any;
    departmentNotes?: any[];
}

export function resolveIfuVersion(
    info: any,
    regionalInfo: RegionalInfo
): string {
    const sourceDates = (regionalInfo.sources ?? [])
        .map((source) => source.documentDate)
        .filter(Boolean)
        .map((date) => new Date(date.toString()))
        .filter((date) => !Number.isNaN(date.getTime()));

    if (sourceDates.length) {
        const latest = new Date(
            Math.max(...sourceDates.map((date) => date.getTime()))
        );
        return latest.toLocaleDateString();
    }

    const implantDocDate = info[SETTING_KEYS.docDate];
    if (implantDocDate) {
        const date = new Date(implantDocDate.toString());
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString();
        }
    }

    const ifu = regionalInfo.ifu;
    return typeof ifu === 'object' && ifu !== null
        ? (ifu as any).version || ''
        : '';
}

export function buildImplantHeaderConfig(
    input: ImplantPreviewInput,
    t: TFunc
): PreviewHeaderConfig {
    const { info, regionalInfo } = input;
    const model = regionalInfo.model || info[SETTING_KEYS.model] || '';
    const manufacturer = info[SETTING_KEYS.manufacturer] || '';
    const type = info[SETTING_KEYS.type] || '';
    const mriCode = info[SETTING_KEYS.mriCode] || '';

    const ifuVersion = resolveIfuVersion(info, regionalInfo);

    const meta: PreviewFieldData[] = [];
    if (mriCode) {
        meta.push({
            key: 'mriCode',
            label: t('implant.preview.mriCode'),
            value: mriCode,
        });
    }
    if (ifuVersion) {
        meta.push({
            key: 'ifuVersion',
            label: t('implant.preview.ifuVersion'),
            value: ifuVersion,
        });
    }
    const lastCheckedDate = resolveLastCheckedDate(info, regionalInfo);
    if (lastCheckedDate) {
        meta.push({
            key: 'lastChecked',
            label: t('implant.preview.lastChecked'),
            value: formatPreviewDate(
                new Date(lastCheckedDate as any).toISOString().slice(0, 10)
            ),
        });
    }

    return {
        title: model,
        subtitle: manufacturer,
        description: type,
        meta,
        generatedDate: new Date().toISOString().slice(0, 10),
        statusBadge: undefined,
        eyebrow: t('implant.preview.eyebrow'),
        brandLine: t('implant.preview.brandLine'),
        summaryLabel: '',
    };
}

export function buildImplantPreviewSections(
    input: ImplantPreviewInput,
    t: TFunc
): PreviewSectionData[] {
    const { info, regionalInfo, conditionals, approvalInfo, departmentNotes } =
        input;
    const sections: PreviewSectionData[] = [];

    if (approvalInfo?.status === 'Approved') {
        const facilityName = approvalInfo.facilityName || approvalInfo.facility;
        const approver = approvalInfo.approvedBy;
        const date = approvalInfo.approvedAt
            ? formatPreviewDate(
                  new Date(approvalInfo.approvedAt).toISOString().slice(0, 10)
              )
            : null;
        const comment = approvalInfo.comment;

        sections.push({
            key: 'approval',
            title: t('implant.preview.localApproval'),
            customContent: (
                <div className={previewTheme.callout}>
                    <div className="flex items-center gap-2.5 border-b border-[#ececea] px-[18px] py-3">
                        <Check
                            className="h-[17px] w-[17px] shrink-0 text-spot"
                            strokeWidth={2.6}
                        />
                        <span className="font-serif text-[17px] font-bold">
                            {facilityName
                                ? t('implant.preview.approvedAt', {
                                      facility: facilityName,
                                  })
                                : t('implant.preview.approvedForScanning')}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-x-10 gap-y-3 px-[18px] py-3.5">
                        {approver && (
                            <div>
                                <div className={previewTheme.fieldLabel}>
                                    {t('implant.preview.approver')}
                                </div>
                                <div className="mt-1 text-[13px]">
                                    {approver}
                                </div>
                            </div>
                        )}
                        {date && (
                            <div>
                                <div className={previewTheme.fieldLabel}>
                                    {t('preview.common.date')}
                                </div>
                                <div className="mt-1 text-[13px]">{date}</div>
                            </div>
                        )}
                        {comment && (
                            <div className="min-w-[40%] flex-1">
                                <div className={previewTheme.fieldLabel}>
                                    {t('implant.preview.comment')}
                                </div>
                                <div className="mt-1 text-[13px]">
                                    {comment}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ),
        });
    }

    const notesWithContent = (departmentNotes ?? []).filter(
        (note: any) => note?.title || stripHtml(note?.note || '').trim()
    );
    if (notesWithContent.length > 0) {
        sections.push({
            key: 'departmentNotes',
            title: t('implant.preview.departmentNotes'),
            customContent: (
                <div className="space-y-3.5">
                    {notesWithContent.map((note: any) => {
                        const addedBy = note.editLog?.[0]?.name;
                        return (
                            <div
                                key={note._id}
                                className={cn(
                                    previewTheme.callout,
                                    'px-[18px] py-3.5'
                                )}
                            >
                                <div className="flex items-baseline justify-between gap-4">
                                    <span className="font-serif text-[15px] font-bold">
                                        {note.title}
                                    </span>
                                    {addedBy && (
                                        <span className="shrink-0 text-[11px] text-[#888]">
                                            {t('implant.preview.addedBy', {
                                                name: addedBy,
                                            })}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5 text-[12.5px] leading-[1.55] text-[#333] [&_p]:!text-[12.5px]">
                                    <PreviewRichText html={note.note || ''} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ),
        });
    }

    if (conditionals && regionalInfo.mriStatus === 2) {
        const mriFields: PreviewFieldData[] = [];

        if (regionalInfo.precaution) {
            const raw = stripHtml(regionalInfo.precaution).trim();
            if (raw) {
                mriFields.push({
                    key: 'precaution',
                    label: 'Precaution',
                    value: <PreviewRichText html={regionalInfo.precaution} />,
                });
            }
        }

        const formatted = formatMRIParameters(conditionals);
        for (const [label, rawValue] of Object.entries(formatted)) {
            if (label === 'Precautions') continue;

            const listValue = renderConditionalsListValue(
                label,
                conditionals,
                t
            );
            if (listValue) {
                mriFields.push({
                    key: `mri-${label}`,
                    label,
                    value: listValue,
                });
                continue;
            }

            if (Array.isArray(rawValue) && rawValue.length === 0) continue;
            const value = rawValue as string;
            if (!value || value === '<p></p>') continue;

            mriFields.push({
                key: `mri-${label}`,
                label,
                value: renderFieldValue(value),
            });
        }

        if (
            regionalInfo.customFields?.some((f: any) => f.isCritical && f.value)
        ) {
            for (const field of regionalInfo.customFields.filter(
                (f: any) => f.isCritical && f.value
            )) {
                mriFields.push({
                    key: `critical-${field.heading}`,
                    label: field.heading,
                    value: renderFieldValue(field.value),
                });
            }
        }

        if (mriFields.length > 0) {
            sections.push({
                key: 'mriConditions',
                title: t('implant.preview.mriConditions'),
                fields: mriFields,
                divided: true,
            });
        }
    }

    const nonCriticalCustomFields = (regionalInfo.customFields ?? []).filter(
        (f: any) => !f.isCritical && f.heading && f.value
    );
    if (nonCriticalCustomFields.length > 0) {
        sections.push({
            key: 'customFields',
            title: t('implant.preview.additionalInfo'),
            fields: nonCriticalCustomFields.map((f: any) => ({
                key: `custom-${f.heading}`,
                label: f.heading,
                value: renderFieldValue(f.value),
            })),
        });
    }

    if (info[SETTING_KEYS.notes]) {
        const notesLabel = t('implant.preview.notes');
        sections.push({
            key: 'notes',
            title: notesLabel,
            fields: [
                {
                    key: 'notes',
                    label: notesLabel,
                    value: renderFieldValue(info[SETTING_KEYS.notes]),
                },
            ],
        });
    }

    const sources: ISource[] = regionalInfo.sources ?? [];
    if (sources.length > 0) {
        sections.push({
            key: 'sources',
            title: t('implant.preview.sources'),
            fields: sources.map((source, i) => ({
                key: `source-${i}`,
                label:
                    source.title || t('implant.preview.sourceN', { n: i + 1 }),
                value: [
                    source.content,
                    source.documentDate
                        ? t('implant.preview.documentDate', {
                              date: new Date(
                                  source.documentDate.toString()
                              ).toLocaleDateString(),
                          })
                        : '',
                ]
                    .filter(Boolean)
                    .join(' — '),
            })),
        });
    }

    return sections;
}
